// Sidebar component for InfluenceHub MVP
// Provides secondary navigation and quick actions
// Different sidebar content for brands vs influencers

import React from 'react';
import { useAuth } from '../../context/AuthContext.jsx';
import { useCurrentInfluencerProfile } from '../../hooks/useCurrentInfluencerProfile';

const Sidebar = ({ onNavigate }) => {
  const { profile } = useAuth();
  const { influencerProfile } = useCurrentInfluencerProfile();

  // Brand navigation items
  const brandNavItems = [
    { id: 'dashboard', label: 'Dashboard', icon: '📊' },
    { id: 'create-campaign', label: 'Create Campaign', icon: '➕' },
    { id: 'campaigns', label: 'My Campaigns', icon: '📋' },
    { id: 'applications', label: 'Applications', icon: '📝' },
    { id: 'settings', label: 'Settings', icon: '⚙️' }
  ];

  // Influencer navigation items (updated for influencer-first MVP)
  // Dashboard removed - users land directly on Profile Builder
  const influencerNavItems = [
    { id: 'profile-edit', label: 'Edit Profile', icon: '✏️' },
    { id: 'settings', label: 'Settings', icon: '⚙️' }
  ];

  // Get navigation items based on user role
  const navItems = profile?.userType === 'brand' ? brandNavItems : influencerNavItems;

  const handleNavClick = (itemId) => {
    if (itemId === 'create-campaign') {
      onNavigate('create-campaign');
    } else if (itemId === 'dashboard') {
      // Dashboard route - only for brands (influencers go directly to Profile Builder)
      if (profile?.userType === 'brand') {
        onNavigate('dashboard');
      } else {
        // For influencers, redirect to Profile Builder
        onNavigate('profile-edit');
      }
    } else if (itemId === 'profile-edit') {
      onNavigate('profile-edit');
    } else if (itemId === 'campaigns') {
      onNavigate('my-campaigns');
    } else if (itemId === 'applications') {
      // Navigate based on user type
      if (profile?.userType === 'brand') {
        onNavigate('brand-applications');
      } else if (profile?.userType === 'influencer') {
        onNavigate('influencer-applications');
      }
    } else if (itemId === 'settings') {
      onNavigate('settings');
    } else if (itemId === 'explore') {
      onNavigate('explore-campaigns');
    } else {
      // TODO: Implement navigation to other sections
      console.log(`Navigate to ${itemId}`);
    }
  };

  return (
    <aside className="w-64 bg-gray-50 border-r border-gray-200 min-h-screen">
      <div className="p-6">
        {/* User profile summary */}
        {profile && (() => {
          // For influencers, use influencerProfile (single source of truth)
          // For brands, use profile from AuthContext
          const isInfluencer = profile.userType === 'influencer';
          const effectiveProfile = isInfluencer ? influencerProfile : profile;
          
          // Get profile picture/logo URL
          // For influencers: use photoURL from influencerProfile (single source of truth)
          // For brands: logoUrl from profile
          const avatarUrl = isInfluencer
            ? (effectiveProfile?.photoURL || effectiveProfile?.profileImageUrl || effectiveProfile?.avatarUrl || null)
            : (profile.logoUrl || profile.photoUrl || profile.profile?.media?.profilePictureUrl || profile.profilePictureUrl || profile.avatarUrl || profile.photoURL || null);
          
          const displayName = isInfluencer
            ? (effectiveProfile?.displayName || effectiveProfile?.identity?.displayName || profile.displayName || 'User')
            : (profile.displayName || profile.companyName || profile.name || 'User');
          
          return (
            <div className="mb-6">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center overflow-hidden relative flex-shrink-0">
                  {avatarUrl ? (
                    <img 
                      src={avatarUrl} 
                      alt={displayName}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        // Fallback to icon if image fails to load
                        if (e.target) {
                          e.target.style.display = 'none';
                          const fallback = e.target.parentElement?.querySelector('.sidebar-avatar-fallback');
                          if (fallback) fallback.style.display = 'flex';
                        }
                      }}
                    />
                  ) : null}
                  <div className={`sidebar-avatar-fallback absolute inset-0 flex items-center justify-center ${avatarUrl ? 'hidden' : ''}`}>
                    <svg className="h-5 w-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {displayName}
                  </p>
                  <p className="text-xs text-gray-500 capitalize">
                    {profile.userType}
                  </p>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Navigation menu items */}
        <nav className="space-y-2">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => handleNavClick(item.id)}
              className="w-full flex items-center space-x-3 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-200 hover:text-gray-900 rounded-md transition-colors"
            >
              <span className="text-lg">{item.icon}</span>
              <span>{item.label}</span>
            </button>
          ))}
        </nav>

      </div>
    </aside>
  );
};

export default Sidebar;
